// Il grassetto delle cartelle email viaggia negli eventi ActionCable
// (Labels::UnreadStateEvent): `label_unread_state` porta
// { inbox_id, labels: [{ id, open_unread }], at, complete }. Il pannello lo
// applica allo store senza chiamate HTTP. Prima ogni evento della casella
// ricaricava l'intera lista cartelle in ogni scheda, anche nascosta, con un
// debounce di 3 s (29.443 chiamate in 12 ore il 27/10/2026).
//
// Resta una ricarica di sicurezza della lista, al massimo una ogni 60 s per
// casella e solo a scheda visibile: quando l'evento non porta lo stato (backend
// vecchio durante il rilascio), quando non e' completo (spostamento di cui il
// server non conosce la cartella di partenza) e quando la scheda torna visibile
// dopo piu' di 60 s con eventi arrivati nel frattempo.

export const LABEL_UNREAD_STATE_KEY = 'label_unread_state';
export const LABEL_SAFETY_REFRESH_INTERVAL_MS = 60000;

const toKey = (value) =>
  value === null || value === undefined || value === '' ? null : String(value);

const toStamp = (value) => {
  const stamp = Number(value);
  return Number.isFinite(stamp) && stamp > 0 ? stamp : null;
};

// Stato normalizzato dell'evento, o null se l'evento non lo porta.
export const readLabelUnreadState = (payload = {}) => {
  const state = payload?.[LABEL_UNREAD_STATE_KEY];
  if (!state || typeof state !== 'object' || !Array.isArray(state.labels)) {
    return null;
  }

  const inboxId = toKey(state.inbox_id);
  if (!inboxId) return null;

  return {
    inboxId,
    at: toStamp(state.at),
    complete: state.complete !== false,
    labels: state.labels
      .filter((label) => toKey(label?.id) !== null)
      .map((label) => ({
        id: toKey(label.id),
        openUnread: label.open_unread === true,
      })),
  };
};

// Tiene per ogni cartella l'istante della lettura piu' recente applicata: due
// eventi della stessa conversazione possono arrivare in ordine inverso, e quello
// piu' vecchio non deve rimettere il grassetto appena tolto.
export const takeFreshLabelUpdates = (stamps, state) => {
  if (!state) return [];
  if (!(stamps instanceof Map) || state.at === null) return state.labels;

  return state.labels.filter((label) => {
    const key = `${state.inboxId}:${label.id}`;
    const previous = stamps.get(key);
    if (previous !== undefined && previous > state.at) return false;
    stamps.set(key, state.at);
    return true;
  });
};

const ownUnreadOf = (label) => {
  if (typeof label.own_unread === 'boolean') return label.own_unread;
  // Risposta senza own_unread (backend vecchio): per una cartella senza
  // sottocartelle has_unread e' gia' il suo stato.
  if (!label.sub_labels?.length) return Boolean(label.has_unread);
  return null;
};

// Applica gli stati all'albero delle cartelle della casella (lo modifica) e
// ricalcola has_unread dei genitori: una cartella e' in grassetto se lo e' lei
// o una sua sottocartella, come in LabelSerializer.
// Ritorna { applied, unresolved }: unresolved quando un genitore non porta
// own_unread e non si puo' ricalcolare (serve la ricarica di sicurezza).
export const applyLabelUnreadUpdates = (labels, updates = []) => {
  const result = { applied: 0, unresolved: false };
  if (!Array.isArray(labels) || !updates.length) return result;

  const byId = new Map(updates.map((update) => [update.id, update.openUnread]));

  const visit = (labelList) => {
    let anyUnread = false;
    labelList.forEach((label) => {
      if (!label) return;
      const children = Array.isArray(label.sub_labels) ? label.sub_labels : [];
      const childUnread = children.length ? visit(children) : false;
      const key = toKey(label.id);

      if (key !== null && byId.has(key)) {
        label.own_unread = byId.get(key);
        result.applied += 1;
      }

      const own = ownUnreadOf(label);
      let hasUnread;
      if (own !== null) {
        hasUnread = own || childUnread;
      } else if (childUnread) {
        hasUnread = true;
      } else {
        // Il genitore puo' essere in grassetto per se' o per le figlie: senza
        // own_unread non si sa. Resta com'e' finche' la ricarica non lo dice.
        hasUnread = Boolean(label.has_unread);
        if (hasUnread) result.unresolved = true;
      }

      if (label.has_unread !== hasUnread) label.has_unread = hasUnread;
      if (hasUnread) anyUnread = true;
    });
    return anyUnread;
  };

  visit(labels);
  return result;
};

const defaultNow = () => Date.now();

const defaultIsVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

// Ricarica di sicurezza della lista cartelle, per casella: al massimo una ogni
// intervalMs, e mai a scheda nascosta.
// - request(inboxId): serve una ricarica. Se l'ultima e' di piu' di intervalMs
//   fa subito, altrimenti una sola in coda allo scadere; a scheda nascosta resta
//   in attesa del ritorno.
// - noteActivity(inboxId): un evento ha aggiornato il grassetto senza ricaricare.
// - onVisibilityChange(): al ritorno visibile ricarica le caselle in attesa o con
//   eventi arrivati, se sono passati piu' di intervalMs dall'ultima.
export const createLabelSafetyRefresh = ({
  fetchLabels,
  isVisible = defaultIsVisible,
  now = defaultNow,
  schedule,
  clear,
  intervalMs = LABEL_SAFETY_REFRESH_INTERVAL_MS,
} = {}) => {
  const startedAt = now();
  const inboxes = new Map();

  const stateOf = (inboxId) => {
    const key = toKey(inboxId);
    if (!inboxes.has(key)) {
      inboxes.set(key, {
        inboxId,
        lastFetchAt: startedAt,
        timer: null,
        needed: false,
        active: false,
      });
    }
    return inboxes.get(key);
  };

  const cancelTimer = (state) => {
    if (state.timer !== null) {
      if (typeof clear === 'function') clear(state.timer);
      state.timer = null;
    }
  };

  const fetchNow = (state) => {
    cancelTimer(state);
    state.lastFetchAt = now();
    state.needed = false;
    state.active = false;
    fetchLabels(state.inboxId);
  };

  const elapsed = (state) => now() - state.lastFetchAt;

  const request = (inboxId) => {
    if (toKey(inboxId) === null) return;
    const state = stateOf(inboxId);
    state.needed = true;
    if (!isVisible()) return;

    if (elapsed(state) >= intervalMs) {
      fetchNow(state);
      return;
    }
    if (state.timer !== null || typeof schedule !== 'function') return;

    state.timer = schedule(
      () => {
        state.timer = null;
        if (state.needed && isVisible()) fetchNow(state);
      },
      intervalMs - elapsed(state)
    );
  };

  const noteActivity = (inboxId) => {
    if (toKey(inboxId) === null) return;
    stateOf(inboxId).active = true;
  };

  const onVisibilityChange = () => {
    if (!isVisible()) return;
    inboxes.forEach((state) => {
      if (!state.needed && !state.active) return;
      if (elapsed(state) > intervalMs) {
        fetchNow(state);
      } else if (state.needed) {
        request(state.inboxId);
      }
    });
  };

  const dispose = () => {
    inboxes.forEach(cancelTimer);
    inboxes.clear();
  };

  return { request, noteActivity, onVisibilityChange, dispose };
};
